// src/pages/admin/inhouse/PurchaseView.jsx
import React, { useState, useEffect, useContext, useCallback } from "react";
import { TranslatorContext } from "../../../context/Translator";
import { SidebarContext } from "../../../context/Sidebar";
import { Row, Col, Card, Button, Table, Badge, Form, Alert, Modal } from "react-bootstrap";
import PageLayout from "../../../layouts/PageLayout";
import axios from "axios";
import { useNavigate, useParams } from "react-router-dom";
import {
    FiArrowLeft, FiPrinter, FiDollarSign, FiEdit, FiSave, FiX,
    FiPlus, FiTrash2, FiCheck, FiRefreshCw
} from "react-icons/fi";
import moment from "moment";
import Select from "react-select";
import Swal from "sweetalert2";

export default function InHousePurchaseView() {
    const { t } = useContext(TranslatorContext);
    const { setSidebar } = useContext(SidebarContext);
    const navigate = useNavigate();
    const { id } = useParams();
    const AuthToken = localStorage.getItem("authToken");

    const [loading, setLoading] = useState(true);
    const [purchase, setPurchase] = useState(null);
    const [vendors, setVendors] = useState([]);
    const [products, setProducts] = useState([]);
    const [paymentMethods, setPaymentMethods] = useState([]);

    // Edit mode state
    const [isEditing, setIsEditing] = useState(false);
    const [editFormData, setEditFormData] = useState(null);
    const [submitting, setSubmitting] = useState(false);
    const [errors, setErrors] = useState({});
    const [submitError, setSubmitError] = useState(null);

    // Payment modal state
    const [showPaymentModal, setShowPaymentModal] = useState(false);
    const [paymentForm, setPaymentForm] = useState({
        payment_method_id: '',
        amount: 0,
        payment_date: new Date().toISOString().split('T')[0],
        notes: ''
    });
    const [paymentSubmitting, setPaymentSubmitting] = useState(false);

    const permissions = JSON.parse(localStorage.getItem("permissions")) || [];
    const hasPermission = (perm) => permissions.includes(perm);

    // Fetch data
    const fetchData = useCallback(async () => {
        setLoading(true);
        try {
            const response = await axios.get(`${process.env.REACT_APP_API_URL}/inhouse/purchases/${id}`, {
                headers: { Authorization: `Bearer ${AuthToken}` }
            });

            if (response.data.status) {
                setPurchase(response.data.data);
                setVendors(response.data.vendors || []);
                setProducts(response.data.products || []);
                setPaymentMethods(response.data.payment_methods || []);

                // Initialize edit form data
                setEditFormData({
                    vendor_id: response.data.data.vendor?.id || null,
                    purchase_date: response.data.data.purchase?.purchase_date || '',
                    notes: response.data.data.purchase?.notes || '',
                    items: response.data.data.items?.map(item => ({
                        id: item.id,
                        product_id: item.product_id,
                        purchase_qty: item.purchase_qty,
                        extra_loose_qty: item.extra_loose_qty || 0,
                        purchase_price: item.purchase_price,
                        sale_price: item.sale_price,
                        expiry_date: item.expiry_date || null
                    })) || [],
                    payments: response.data.data.payments?.map(payment => ({
                        id: payment.id,
                        payment_method_id: payment.payment_method_id,
                        amount: payment.amount,
                        payment_date: payment.payment_date,
                        notes: payment.notes
                    })) || []
                });
            }
        } catch (err) {
            console.error("Error fetching purchase:", err);
            Swal.fire({
                toast: true,
                position: 'top-end',
                icon: 'error',
                title: t('Failed to load purchase'),
                showConfirmButton: false,
                timer: 2000
            });
        } finally {
            setLoading(false);
        }
    }, [id, AuthToken, t]);

    useEffect(() => {
        setSidebar(false);
        fetchData();
    }, [setSidebar, fetchData]);

    // Calculate line total including loose quantity
    const calculateLineTotal = (item, selectedProduct) => {
        const qty = parseFloat(item.purchase_qty) || 0;
        const price = parseFloat(item.purchase_price) || 0;
        const extraLooseQty = parseFloat(item.extra_loose_qty) || 0;

        // Calculate box/packet total
        const boxTotal = qty * price;

        // Calculate loose items value based on per-unit price
        let looseTotal = 0;
        if (selectedProduct && selectedProduct.unit_quantity > 0 && extraLooseQty > 0) {
            const unitPrice = price / selectedProduct.unit_quantity;
            looseTotal = extraLooseQty * unitPrice;
        }

        return boxTotal + looseTotal;
    };

    // Calculate total stock added (for display)
    const calculateTotalStockAdded = (item, selectedProduct) => {
        const qty = parseFloat(item.purchase_qty) || 0;
        const extraLooseQty = parseFloat(item.extra_loose_qty) || 0;
        const unitQuantity = selectedProduct?.unit_quantity || 1;

        return (qty * unitQuantity) + extraLooseQty;
    };

    // Calculate grand total
    const calculateGrandTotal = (items) => {
        return items.reduce((sum, item) => sum + calculateLineTotal(item), 0);
    };

    // Calculate total paid
    const calculateTotalPaid = (payments) => {
        return payments.reduce((sum, payment) => sum + (parseFloat(payment.amount) || 0), 0);
    };

    // Get remaining amount
    const getRemainingAmount = (items, payments) => {
        return Math.max(0, calculateGrandTotal(items) - calculateTotalPaid(payments));
    };

    // Handle edit form changes
    const handleEditChange = (e) => {
        const { name, value } = e.target;
        setEditFormData(prev => ({ ...prev, [name]: value }));
        if (errors[name]) setErrors(prev => ({ ...prev, [name]: "" }));
    };

    const handleVendorChange = (selectedOption) => {
        setEditFormData(prev => ({ ...prev, vendor_id: selectedOption ? selectedOption.value : null }));
        if (errors.vendor_id) setErrors(prev => ({ ...prev, vendor_id: "" }));
    };

    const handleItemChange = (index, field, value) => {
        const updatedItems = [...editFormData.items];
        updatedItems[index][field] = value;

        if (field === 'product_id' && value) {
            const selectedProduct = products.find(p => p.id === value);
            if (selectedProduct) {
                updatedItems[index].purchase_price = selectedProduct.default_purchase_price;
                updatedItems[index].sale_price = selectedProduct.default_sale_price;
            }
        }

        setEditFormData(prev => ({ ...prev, items: updatedItems }));

        // Auto-update payment amounts
        const newGrandTotal = calculateGrandTotal(updatedItems);
        const currentPaid = calculateTotalPaid(editFormData.payments);

        if (currentPaid > newGrandTotal && editFormData.payments.length > 0) {
            const updatedPayments = [...editFormData.payments];
            updatedPayments[0].amount = newGrandTotal;
            setEditFormData(prev => ({ ...prev, payments: updatedPayments }));
        }
    };

    // Stock badge color
    const getStockBadgeClass = (stock, minAlert) => {
        if (stock <= 0) return "bg-danger";
        if (stock <= minAlert) return "bg-warning";
        return "bg-success";
    };

    const addItemRow = () => {
        setEditFormData(prev => ({
            ...prev,
            items: [...prev.items, {
                product_id: null,
                purchase_qty: 1,
                extra_loose_qty: 0,
                purchase_price: 0,
                sale_price: 0,
                expiry_date: null
            }]
        }));
    };

    const removeItemRow = (index) => {
        if (editFormData.items.length === 1) {
            Swal.fire({
                toast: true,
                position: 'top-end',
                icon: 'warning',
                title: t('At least one product is required'),
                showConfirmButton: false,
                timer: 2000
            });
            return;
        }
        const updatedItems = editFormData.items.filter((_, i) => i !== index);
        setEditFormData(prev => ({ ...prev, items: updatedItems }));
    };

    const handlePaymentChange = (index, field, value) => {
        const updatedPayments = [...editFormData.payments];
        updatedPayments[index][field] = value;
        setEditFormData(prev => ({ ...prev, payments: updatedPayments }));
    };

    const addPaymentRow = () => {
        const remaining = getRemainingAmount(editFormData.items, editFormData.payments);
        if (remaining <= 0) {
            Swal.fire({
                toast: true,
                position: 'top-end',
                icon: 'info',
                title: t('Purchase is already fully paid'),
                showConfirmButton: false,
                timer: 2000
            });
            return;
        }
        setEditFormData(prev => ({
            ...prev,
            payments: [...prev.payments, {
                payment_method_id: '',
                amount: remaining,
                payment_date: new Date().toISOString().split('T')[0],
                notes: null
            }]
        }));
    };

    const removePaymentRow = (index) => {
        const updatedPayments = editFormData.payments.filter((_, i) => i !== index);
        setEditFormData(prev => ({ ...prev, payments: updatedPayments }));
    };

    // Submit edit
    const handleEditSubmit = async () => {
        setSubmitting(true);
        setErrors({});
        setSubmitError(null);

        // Validation
        const validationErrors = {};
        if (!editFormData.vendor_id) validationErrors.vendor_id = t("Vendor is required");
        if (!editFormData.items.length) validationErrors.items = t("At least one product is required");

        editFormData.items.forEach((item, index) => {
            if (!item.product_id) validationErrors[`items.${index}.product_id`] = t("Product is required");
            if (!item.purchase_qty || item.purchase_qty <= 0) validationErrors[`items.${index}.purchase_qty`] = t("Valid quantity required");
            if (!item.purchase_price || item.purchase_price < 0) validationErrors[`items.${index}.purchase_price`] = t("Valid price required");
        });

        if (Object.keys(validationErrors).length > 0) {
            setErrors(validationErrors);
            setSubmitting(false);
            return;
        }

        const submitData = {
            vendor_id: editFormData.vendor_id,
            purchase_date: editFormData.purchase_date,
            notes: editFormData.notes,
            items: editFormData.items.map(item => ({
                product_id: item.product_id,
                purchase_qty: parseFloat(item.purchase_qty),
                extra_loose_qty: parseFloat(item.extra_loose_qty) || 0,
                purchase_price: parseFloat(item.purchase_price),
                sale_price: parseFloat(item.sale_price) || 0,
                expiry_date: item.expiry_date || null
            })),
            payments: editFormData.payments
                .filter(p => p.payment_method_id && parseFloat(p.amount) > 0)
                .map(p => ({
                    id: p.id,
                    payment_method_id: p.payment_method_id,
                    amount: parseFloat(p.amount),
                    payment_date: p.payment_date,
                    notes: p.notes
                }))
        };

        try {
            const response = await axios.put(
                `${process.env.REACT_APP_API_URL}/inhouse/purchases/${id}`,
                submitData,
                { headers: { Authorization: `Bearer ${AuthToken}` } }
            );

            if (response.data.status) {
                Swal.fire({
                    icon: 'success',
                    title: t('Purchase Updated!'),
                    text: t('Purchase has been updated successfully.'),
                    confirmButtonColor: '#3085d6',
                    confirmButtonText: t('OK')
                });
                setIsEditing(false);
                fetchData();
            } else {
                setSubmitError(response.data.message || t('Failed to update purchase'));
            }
        } catch (err) {
            if (err.response?.status === 422) {
                setErrors(err.response.data.errors);
            } else if (err.response?.data?.message) {
                setSubmitError(err.response.data.message);
            } else {
                setSubmitError(t('An error occurred while updating the purchase'));
            }
        } finally {
            setSubmitting(false);
        }
    };

    // Add payment
    const handleAddPayment = async () => {
        if (!paymentForm.payment_method_id) {
            Swal.fire({
                toast: true,
                position: 'top-end',
                icon: 'warning',
                title: t('Please select payment method'),
                showConfirmButton: false,
                timer: 2000
            });
            return;
        }

        if (paymentForm.amount <= 0) {
            Swal.fire({
                toast: true,
                position: 'top-end',
                icon: 'warning',
                title: t('Amount must be greater than 0'),
                showConfirmButton: false,
                timer: 2000
            });
            return;
        }

        setPaymentSubmitting(true);
        try {
            const response = await axios.post(
                `${process.env.REACT_APP_API_URL}/inhouse/purchases/${id}/payments`,
                paymentForm,
                { headers: { Authorization: `Bearer ${AuthToken}` } }
            );

            if (response.data.status) {
                Swal.fire({
                    toast: true,
                    position: 'top-end',
                    icon: 'success',
                    title: t('Payment added successfully'),
                    showConfirmButton: false,
                    timer: 2000
                });
                setShowPaymentModal(false);
                fetchData();
                setPaymentForm({
                    payment_method_id: '',
                    amount: 0,
                    payment_date: new Date().toISOString().split('T')[0],
                    notes: ''
                });
            }
        } catch (err) {
            Swal.fire({
                toast: true,
                position: 'top-end',
                icon: 'error',
                title: err.response?.data?.message || t('Failed to add payment'),
                showConfirmButton: false,
                timer: 3000
            });
        } finally {
            setPaymentSubmitting(false);
        }
    };

    // Delete payment
    const handleDeletePayment = async (paymentId) => {
        const result = await Swal.fire({
            title: t('Are you sure?'),
            text: t('This payment will be permanently deleted'),
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#d33',
            cancelButtonColor: '#3085d6',
            confirmButtonText: t('Yes, delete it!'),
            cancelButtonText: t('Cancel')
        });

        if (result.isConfirmed) {
            try {
                await axios.delete(
                    `${process.env.REACT_APP_API_URL}/inhouse/purchases/${id}/payments/${paymentId}`,
                    { headers: { Authorization: `Bearer ${AuthToken}` } }
                );

                Swal.fire({
                    toast: true,
                    position: 'top-end',
                    icon: 'success',
                    title: t('Payment deleted successfully'),
                    showConfirmButton: false,
                    timer: 2000
                });
                fetchData();
            } catch (err) {
                Swal.fire({
                    toast: true,
                    position: 'top-end',
                    icon: 'error',
                    title: t('Failed to delete payment'),
                    showConfirmButton: false,
                    timer: 2000
                });
            }
        }
    };

    const getPaymentStatusBadge = (status) => {
        switch (status) {
            case 'paid':
                return <Badge bg="success" className="p-2">{t('Paid')}</Badge>;
            case 'partial':
                return <Badge bg="warning" className="p-2">{t('Partial')}</Badge>;
            case 'unpaid':
                return <Badge bg="danger" className="p-2">{t('Unpaid')}</Badge>;
            default:
                return <Badge bg="secondary" className="p-2">{t('Unknown')}</Badge>;
        }
    };

    // Vendor options
    const vendorOptions = vendors.map(v => ({ value: v.id, label: `${v.name} ${v.company_name ? `(${v.company_name})` : ''}` }));
    const selectedVendor = vendorOptions.find(opt => opt.value === editFormData?.vendor_id);

    // Product options
    const getProductOptions = (currentIndex) => {
        const selectedProductIds = editFormData?.items
            ?.filter((_, idx) => idx !== currentIndex && _.product_id)
            .map(_ => _.product_id) || [];

        return products
            .filter(p => !selectedProductIds.includes(p.id))
            .map(p => ({ value: p.id, label: p.display_label }));
    };

    if (loading) {
        return (
            <PageLayout>
                <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '400px' }}>
                    <div className="text-center">
                        <div className="spinner-border text-primary" role="status" style={{ width: '3rem', height: '3rem' }} />
                        <p className="mt-3 text-muted">{t("Loading purchase details...")}</p>
                    </div>
                </div>
            </PageLayout>
        );
    }

    if (!purchase) {
        return (
            <PageLayout>
                <div className="text-center p-5">
                    <div className="empty-state">
                        <FiDollarSign size={64} className="text-muted mb-3" />
                        <h5>{t("Purchase not found")}</h5>
                        <Button onClick={() => navigate("/inhouse/purchases")} className="mt-3">
                            {t("Back to Purchases")}
                        </Button>
                    </div>
                </div>
            </PageLayout>
        );
    }

    // Edit mode view
    if (isEditing && editFormData) {
        const grandTotal = calculateGrandTotal(editFormData.items);
        const totalPaid = calculateTotalPaid(editFormData.payments);
        const remainingAmount = getRemainingAmount(editFormData.items, editFormData.payments);

        return (
            <PageLayout>
                <div className="container-fluid px-0">
                    {/* Header */}
                    <div className="d-flex justify-content-between align-items-center mb-4 flex-wrap gap-3">
                        <div>
                            <h4 className="mb-1">{t("Edit Purchase")}</h4>
                            <p className="text-muted mb-0">{purchase.purchase?.purchase_number}</p>
                        </div>
                        <div className="d-flex gap-2">
                            <Button
                                variant="secondary"
                                onClick={() => {
                                    setIsEditing(false);
                                    fetchData();
                                }}
                                className="d-flex align-items-center gap-2"
                            >
                                <FiX size={18} /> {t("Cancel")}
                            </Button>
                            <Button
                                variant="primary"
                                onClick={handleEditSubmit}
                                disabled={submitting}
                                className="d-flex align-items-center gap-2"
                            >
                                {submitting ? (
                                    <>
                                        <div className="spinner-border spinner-border-sm" role="status" />
                                        <span>{t("Saving...")}</span>
                                    </>
                                ) : (
                                    <>
                                        <FiSave /> {t("Save Changes")}
                                    </>
                                )}
                            </Button>
                        </div>
                    </div>

                    {submitError && (
                        <Alert variant="danger" className="mb-4" onClose={() => setSubmitError(null)} dismissible>
                            <Alert.Heading>{t("Error")}</Alert.Heading>
                            <p>{submitError}</p>
                        </Alert>
                    )}

                    <Row>
                        <Col lg={8}>
                            {/* Purchase Info Card */}
                            <Card className="border-0 shadow-sm mb-4">
                                <Card.Body className="p-4">
                                    <h5 className="mb-4 pb-2 border-bottom">{t("Purchase Information")}</h5>

                                    <Row className="g-4">
                                        <Col md={6}>
                                            <Form.Group>
                                                <Form.Label className="fw-semibold">
                                                    {t("Vendor")} <span className="text-danger">*</span>
                                                </Form.Label>
                                                <Select
                                                    options={vendorOptions}
                                                    value={selectedVendor}
                                                    onChange={handleVendorChange}
                                                    isClearable
                                                    isSearchable
                                                    placeholder={t("Select vendor...")}
                                                    className="react-select-container"
                                                    classNamePrefix="react-select"
                                                    styles={{
                                                        control: (base) => ({
                                                            ...base,
                                                            borderColor: errors.vendor_id ? '#dc3545' : '#dee2e6',
                                                            minHeight: '38px'
                                                        })
                                                    }}
                                                />
                                                {errors.vendor_id && <div className="text-danger small mt-1">{errors.vendor_id}</div>}
                                            </Form.Group>
                                        </Col>
                                        <Col md={6}>
                                            <Form.Group>
                                                <Form.Label className="fw-semibold">{t("Purchase Date")}</Form.Label>
                                                <Form.Control
                                                    type="date"
                                                    name="purchase_date"
                                                    value={editFormData.purchase_date}
                                                    onChange={handleEditChange}
                                                />
                                            </Form.Group>
                                        </Col>
                                        <Col md={12}>
                                            <Form.Group>
                                                <Form.Label className="fw-semibold">{t("Purchase Number")}</Form.Label>
                                                <Form.Control
                                                    type="text"
                                                    value={purchase.purchase?.purchase_number}
                                                    disabled
                                                    className="bg-light"
                                                />
                                            </Form.Group>
                                        </Col>
                                        <Col md={12}>
                                            <Form.Group>
                                                <Form.Label className="fw-semibold">{t("Notes")}</Form.Label>
                                                <Form.Control
                                                    as="textarea"
                                                    name="notes"
                                                    value={editFormData.notes}
                                                    onChange={handleEditChange}
                                                    rows={3}
                                                    placeholder={t("Additional notes...")}
                                                />
                                            </Form.Group>
                                        </Col>
                                    </Row>

                                    <h5 className="mt-4 mb-4 pb-2 border-bottom">{t("Products")}</h5>


                                    {editFormData.items.map((item, index) => {
                                        const productOptions = getProductOptions(index);
                                        const selectedProduct = products.find(p => p.id === item.product_id);
                                        const lineTotal = calculateLineTotal(item, selectedProduct);
                                        const totalStockAdded = calculateTotalStockAdded(item, selectedProduct);

                                        return (
                                            <div key={index} className="border rounded p-3 mb-3 bg-light">
                                                <Row className="g-3">
                                                    <Col lg={4} md={6}>
                                                        <Form.Group>
                                                            <Form.Label className="small fw-bold mb-2">
                                                                {t("Product")} <span className="text-danger">*</span>
                                                            </Form.Label>
                                                            <Select
                                                                options={productOptions}
                                                                value={productOptions.find(opt => opt.value === item.product_id)}
                                                                onChange={(opt) => handleItemChange(index, 'product_id', opt?.value || null)}
                                                                isClearable
                                                                isSearchable
                                                                placeholder={t("Select product...")}
                                                                className="react-select-container-sm"
                                                                classNamePrefix="react-select"
                                                            />
                                                            {errors[`items.${index}.product_id`] && (
                                                                <div className="text-danger small mt-1">
                                                                    {errors[`items.${index}.product_id`]}
                                                                </div>
                                                            )}
                                                        </Form.Group>
                                                    </Col>

                                                    <Col lg={2} md={3}>
                                                        <Form.Group>
                                                            <Form.Label className="small fw-bold mb-2 d-block">
                                                                {t("Boxes/Packets")}
                                                            </Form.Label>
                                                            <Form.Control
                                                                type="number"
                                                                value={item.purchase_qty}
                                                                onChange={(e) =>
                                                                    handleItemChange(
                                                                        index,
                                                                        'purchase_qty',
                                                                        parseFloat(e.target.value) || 0
                                                                    )
                                                                }
                                                                step="0.01"
                                                                min="0.01"
                                                                className="mb-1"
                                                            />
                                                            <Form.Text className="text-muted">
                                                                {t("Number of purchase units")}
                                                            </Form.Text>
                                                        </Form.Group>
                                                    </Col>

                                                    <Col lg={2} md={2}>
                                                        <Form.Group>
                                                            <Form.Label className="small fw-bold mb-2 d-block">
                                                                {t("Extra Loose Qty")}
                                                            </Form.Label>
                                                            <Form.Control
                                                                type="number"
                                                                value={item.extra_loose_qty}
                                                                onChange={(e) =>
                                                                    handleItemChange(
                                                                        index,
                                                                        'extra_loose_qty',
                                                                        parseFloat(e.target.value) || 0
                                                                    )
                                                                }
                                                                step="0.01"
                                                                min="0"
                                                                className="mb-1"
                                                            />
                                                            <Form.Text className="text-muted">
                                                                {t("Additional loose items")}
                                                            </Form.Text>
                                                        </Form.Group>
                                                    </Col>

                                                    <Col lg={2} md={2}>
                                                        <Form.Group>
                                                            <Form.Label className="small fw-bold mb-2 d-block">
                                                                {t("Price Per Box")}
                                                            </Form.Label>
                                                            <Form.Control
                                                                type="number"
                                                                value={item.purchase_price}
                                                                onChange={(e) =>
                                                                    handleItemChange(
                                                                        index,
                                                                        'purchase_price',
                                                                        parseFloat(e.target.value) || 0
                                                                    )
                                                                }
                                                                step="0.01"
                                                                min="0"
                                                            />
                                                            {selectedProduct && selectedProduct.unit_quantity > 0 && (
                                                                <Form.Text className="text-muted">
                                                                    {t("Unit Price")}: {(item.purchase_price / selectedProduct.unit_quantity)}/{selectedProduct.unit_type}
                                                                </Form.Text>
                                                            )}
                                                        </Form.Group>
                                                    </Col>

                                                    <Col lg={1} md={2}>
                                                        <Form.Group>
                                                            <Form.Label className="small fw-bold mb-2 d-block">
                                                                {t("Sale Price")}
                                                            </Form.Label>
                                                            <Form.Control
                                                                type="number"
                                                                value={item.sale_price}
                                                                onChange={(e) =>
                                                                    handleItemChange(
                                                                        index,
                                                                        'sale_price',
                                                                        parseFloat(e.target.value) || 0
                                                                    )
                                                                }
                                                                step="0.01"
                                                                min="0"
                                                            />
                                                        </Form.Group>
                                                    </Col>

                                                    <Col lg={1} md={2}>
                                                        <div className="pt-4 text-lg-end text-center">
                                                            <div className="small text-muted">
                                                                {t("Total")}
                                                            </div>
                                                            <div className="fw-bold text-primary h4">
                                                                {lineTotal}
                                                            </div>
                                                        </div>
                                                    </Col>

                                                    {/* Stock and Financial Breakdown */}
                                                    {selectedProduct && (
                                                        <Col md={12}>
                                                            <div className="bg-info  rounded p-3">
                                                                <Row>
                                                                    <Col md={6}>
                                                                        <small className="text-dark">
                                                                            <strong>{t("Stock Calculation")}:</strong><br />
                                                                            {item.purchase_qty || 0} {selectedProduct.purchase_unit} × {selectedProduct.unit_quantity}{selectedProduct.unit_type} = {(item.purchase_qty || 0) * selectedProduct.unit_quantity} {selectedProduct.unit_type}
                                                                            {item.extra_loose_qty > 0 && (
                                                                                <>
                                                                                    <br />
                                                                                    + {item.extra_loose_qty} {t("loose")} {selectedProduct.unit_type}
                                                                                    <br />
                                                                                    <strong className="text-success">
                                                                                        = {totalStockAdded} {selectedProduct.unit_type} {t("total stock added")}
                                                                                    </strong>
                                                                                </>
                                                                            )}
                                                                        </small>
                                                                    </Col>
                                                                    {/* <Col md={6}>
                                                                        <small className="text-dark">
                                                                            <strong>{t("Financial Breakdown")}:</strong><br />
                                                                            {item.purchase_qty || 0} {selectedProduct.purchase_unit} × {item.purchase_price} = {(item.purchase_qty || 0) * item.purchase_price}
                                                                            {item.extra_loose_qty > 0 && (
                                                                                <>
                                                                                    <br />
                                                                                    + {item.extra_loose_qty} {t("loose")} × {(item.purchase_price / selectedProduct.unit_quantity)} = {(item.extra_loose_qty * (item.purchase_price / selectedProduct.unit_quantity))}
                                                                                    <br />
                                                                                    <strong className="text-primary">
                                                                                        = {lineTotal} {t("line total")}
                                                                                    </strong>
                                                                                </>
                                                                            )}
                                                                        </small>
                                                                    </Col> */}
                                                                </Row>
                                                            </div>
                                                        </Col>
                                                    )}

                                                    <Col md={12} className="text-end">
                                                        <Button
                                                            variant="danger"
                                                            size="sm"
                                                            onClick={() => removeItemRow(index)}
                                                            className="px-3"
                                                        >
                                                            <FiTrash2 className="me-1" />
                                                            {t("Remove Product")}
                                                        </Button>
                                                    </Col>
                                                </Row>
                                            </div>
                                        );
                                    })}

                                    <Button
                                        variant="primary"
                                        onClick={addItemRow}
                                        className="d-flex align-items-center gap-2 mt-2"
                                    >
                                        <FiPlus /> {t("Add Product")}
                                    </Button>
                                </Card.Body>
                            </Card>
                        </Col>

                        <Col lg={4}>
                            {/* Summary Card */}
                            <Card className="border-0 shadow-sm mb-4">
                                <Card.Body className="p-4">
                                    <h5 className="mb-4 pb-2 border-bottom">{t("Purchase Summary")}</h5>

                                    <div className="d-flex justify-content-between mb-2">
                                        <span className="text-muted">{t("Total Items")}:</span>
                                        <strong>{editFormData.items.length}</strong>
                                    </div>
                                    <div className="d-flex justify-content-between mb-3 pb-3 border-bottom">
                                        <span className="text-muted">{t("Grand Total")}:</span>
                                        <strong className="text-primary h5">{grandTotal}</strong>
                                    </div>

                                    <div className="d-flex justify-content-between mb-2 text-success">
                                        <span>{t("Total Paid")}:</span>
                                        <strong>{totalPaid}</strong>
                                    </div>
                                    <div className="d-flex justify-content-between mb-3 text-danger">
                                        <span>{t("Due Amount")}:</span>
                                        <strong>{remainingAmount}</strong>
                                    </div>
                                </Card.Body>
                            </Card>

                            {/* Payments Card */}
                            <Card className="border-0 shadow-sm">
                                <Card.Body className="p-4">
                                    <div className="d-flex justify-content-between align-items-center mb-4 pb-2 border-bottom">
                                        <h5 className="mb-0">{t("Payments")}</h5>
                                        <Button
                                            variant="success"
                                            size="sm"
                                            onClick={addPaymentRow}
                                            disabled={remainingAmount <= 0}
                                            className="d-flex align-items-center gap-1"
                                        >
                                            <FiPlus size={14} /> {t("Add")}
                                        </Button>
                                    </div>

                                    {editFormData.payments.map((payment, index) => (
                                        <div key={index} className="border rounded p-3 mb-3">
                                            <Row className="g-2">
                                                <Col xs={12}>
                                                    <Form.Group>
                                                        <Form.Label className="small fw-bold">{t("Method")}</Form.Label>
                                                        <Form.Select
                                                            size="sm"
                                                            value={payment.payment_method_id}
                                                            onChange={(e) => handlePaymentChange(index, 'payment_method_id', e.target.value)}
                                                        >
                                                            <option value="">{t("Select method")}</option>
                                                            {paymentMethods.map(pm => (
                                                                <option key={pm.id} value={pm.id}>{pm.name}</option>
                                                            ))}
                                                        </Form.Select>
                                                    </Form.Group>
                                                </Col>
                                                <Col xs={6}>
                                                    <Form.Group>
                                                        <Form.Label className="small fw-bold">{t("Amount")}</Form.Label>
                                                        <Form.Control
                                                            type="number"
                                                            size="sm"
                                                            value={payment.amount}
                                                            onChange={(e) => handlePaymentChange(index, 'amount', parseFloat(e.target.value) || 0)}
                                                            step="0.01"
                                                            min="0"
                                                        />
                                                    </Form.Group>
                                                </Col>
                                                <Col xs={6}>
                                                    <Form.Group>
                                                        <Form.Label className="small fw-bold">{t("Date")}</Form.Label>
                                                        <Form.Control
                                                            type="date"
                                                            size="sm"
                                                            value={payment.payment_date}
                                                            onChange={(e) => handlePaymentChange(index, 'payment_date', e.target.value)}
                                                        />
                                                    </Form.Group>
                                                </Col>
                                                {editFormData.payments.length > 1 && (
                                                    <Col xs={12} className="text-end">
                                                        <Button
                                                            variant="link"
                                                            size="sm"
                                                            className="text-danger p-0"
                                                            onClick={() => removePaymentRow(index)}
                                                        >
                                                            <FiTrash2 /> {t("Remove")}
                                                        </Button>
                                                    </Col>
                                                )}
                                            </Row>
                                        </div>
                                    ))}

                                    <hr />
                                    <div className="d-flex justify-content-between">
                                        <strong>{t("Total Payments")}:</strong>
                                        <strong className="text-success">{totalPaid}</strong>
                                    </div>
                                </Card.Body>
                            </Card>
                        </Col>
                    </Row>
                </div>
            </PageLayout>
        );
    }

    // View mode (Read-only)
    return (
        <PageLayout>
            <div className="container-fluid px-0">
                {/* Header */}
                <div className="d-flex justify-content-between align-items-center mb-4 flex-wrap gap-3">
                    <div>
                        <h4 className="mb-1">{t("Purchase Details")}</h4>
                        <p className="text-muted mb-0">{purchase.purchase?.purchase_number}</p>
                    </div>
                    <div className="d-flex gap-2">
                        <Button
                            variant="secondary"
                            onClick={() => navigate("/inhouse/purchases")}
                            className="d-flex align-items-center gap-2"
                        >
                            <FiArrowLeft /> {t("Back")}
                        </Button>

                        <Button
                            variant="primary"
                            onClick={() => setIsEditing(true)}
                            className="d-flex align-items-center gap-2"
                        >
                            <FiEdit /> {t("Edit")}
                        </Button>

                    </div>
                </div>

                <Row>
                    <Col lg={8}>
                        {/* Purchase Info Card */}
                        <Card className="border-0 shadow-sm mb-4">
                            <Card.Body className="p-4">
                                <h5 className="mb-4 pb-2 border-bottom">{t("Purchase Information")}</h5>

                                <Row>
                                    <Col md={6}>
                                        <div className="mb-3">
                                            <div className="text-muted small">{t("PO Number")}</div>
                                            <div className="fw-bold fs-5">{purchase.purchase?.purchase_number}</div>
                                        </div>
                                        <div className="mb-3">
                                            <div className="text-muted small">{t("Purchase Date")}</div>
                                            <div>{purchase.purchase?.purchase_date}</div>
                                        </div>
                                    </Col>
                                    <Col md={6}>
                                        <div className="mb-3">
                                            <div className="text-muted small">{t("Vendor")}</div>
                                            <div className="fw-bold">{purchase.vendor?.name}</div>
                                            {purchase.vendor?.company_name && (
                                                <div className="small text-muted">{purchase.vendor.company_name}</div>
                                            )}
                                            {purchase.vendor?.phone && (
                                                <div className="small mt-1">
                                                    <span className="text-muted">{t("Phone")}:</span> {purchase.vendor.phone}
                                                </div>
                                            )}
                                            {purchase.vendor?.email && (
                                                <div className="small">
                                                    <span className="text-muted">{t("Email")}:</span> {purchase.vendor.email}
                                                </div>
                                            )}
                                        </div>
                                    </Col>
                                    {purchase.purchase?.notes && (
                                        <Col md={12}>
                                            <div className="mb-3">
                                                <div className="text-muted small">{t("Notes")}</div>
                                                <div className="p-2 bg-light rounded">{purchase.purchase.notes}</div>
                                            </div>
                                        </Col>
                                    )}
                                </Row>
                            </Card.Body>
                        </Card>

                        {/* Products Card */}
                        <Card className="border-0 shadow-sm">
                            <Card.Body className="p-4">
                                <h5 className="mb-4 pb-2 border-bottom">{t("Products")}</h5>

                                <div className="table-responsive">
                                    <Table striped hover className="mb-0">
                                        <thead className="table-light">
                                            <tr>
                                                <th>#</th>
                                                <th>{t("Product")}</th>
                                                <th className="text-end">{t("Boxes/Packets")}</th>
                                                <th className="text-end">{t("Loose Qty")}</th>
                                                <th className="text-end">{t("Unit Price")}</th>
                                                <th className="text-end">{t("Total Stock Added")}</th>
                                                <th className="text-end">{t("Total")}</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {purchase.items?.map((item, index) => (
                                                <tr key={index}>
                                                    <td>{index + 1}</td>
                                                    <td>
                                                        <div className="fw-bold">{item.product_name}</div>
                                                        <small className="text-muted">{item.purchase_unit} × {item.unit_quantity}{item.unit_type}</small>
                                                    </td>
                                                    <td className="text-end">{item.purchase_qty}</td>
                                                    <td className="text-end">{item.extra_loose_qty || 0}</td>
                                                    <td className="text-end">{parseFloat(item.purchase_price)}</td>
                                                    <td className="text-end fw-bold text-success">
                                                        <Badge
                                                            className={getStockBadgeClass(
                                                                item.current_stock,
                                                                item.minimum_alert_qty
                                                            )}
                                                        >
                                                            {(item.current_stock / item.unit_quantity).toFixed(2)}
                                                            {" "}
                                                            {item.purchase_unit}
                                                        </Badge>

                                                        <div>
                                                            {item.total_stock_added} {item.unit_type}
                                                        </div>
                                                    </td>
                                                    <td className="text-end fw-bold">{parseFloat(item.line_total)}</td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </Table>
                                </div>
                            </Card.Body>
                        </Card>
                    </Col>

                    <Col lg={4}>
                        {/* Summary Card */}
                        <Card className="border-0 shadow-sm mb-4">
                            <Card.Body className="p-4">
                                <h5 className="mb-4 pb-2 border-bottom">{t("Financial Summary")}</h5>

                                <div className="d-flex justify-content-between mb-3">
                                    <span className="text-muted">{t("Grand Total")}:</span>
                                    <strong className="h5 text-primary">
                                        {purchase.financial_summary?.grand_total}
                                    </strong>
                                </div>
                                <div className="d-flex justify-content-between mb-3">
                                    <span className="text-muted">{t("Total Paid")}:</span>
                                    <strong className="text-success fs-5">
                                        {purchase.financial_summary?.total_paid}
                                    </strong>
                                </div>
                                <div className="d-flex justify-content-between mb-3 pb-3 border-bottom">
                                    <span className="text-muted">{t("Due Amount")}:</span>
                                    <strong className="text-danger fs-4">
                                        {purchase.financial_summary?.due_amount}
                                    </strong>
                                </div>

                            </Card.Body>
                        </Card>

                        {/* Payment History Card */}
                        {purchase.payments?.length > 0 && (
                            <Card className="border-0 shadow-sm">
                                <Card.Body className="p-4">
                                    <h5 className="mb-4 pb-2 border-bottom">{t("Payment History")}</h5>

                                    {purchase.payments.map((payment, index) => (
                                        <div key={index} className="border-bottom pb-3 mb-3">
                                            <div className="d-flex justify-content-between align-items-start">
                                                <div>
                                                    <div className="fw-bold">{payment.method?.name}</div>
                                                    <div className="small text-muted">{moment(payment.payment_date).format("DD MMM YYYY")}</div>
                                                    {payment.notes && (
                                                        <div className="small text-muted mt-1">{payment.notes}</div>
                                                    )}
                                                </div>
                                                <div className="text-end">
                                                    <div className="fw-bold text-success fs-6">{parseFloat(payment.amount)}</div>

                                                    <Button
                                                        variant="link"
                                                        size="sm"
                                                        className="text-danger p-0 mt-1"
                                                        onClick={() => handleDeletePayment(payment.id)}
                                                    >
                                                        <FiTrash2 size={12} /> {t("Delete")}
                                                    </Button>

                                                </div>
                                            </div>
                                        </div>
                                    ))}

                                    <div className="mt-3 pt-2">
                                        <div className="d-flex justify-content-between">
                                            <strong>{t("Total Payments")}:</strong>
                                            <strong className="text-success">
                                                {purchase.financial_summary?.total_paid}
                                            </strong>
                                        </div>
                                    </div>
                                </Card.Body>
                            </Card>
                        )}
                    </Col>
                </Row>
            </div>

            {/* Payment Modal */}
            <Modal show={showPaymentModal} onHide={() => setShowPaymentModal(false)} centered size="md">
                <Modal.Header closeButton>
                    <Modal.Title>
                        <FiDollarSign className="me-2" />
                        {t("Add Payment")}
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <div className="mb-4 p-3 bg-light rounded">
                        <div className="text-muted small">{t("Due Amount")}</div>
                        <div className="h3 text-danger mb-0">{purchase.financial_summary?.due_amount}</div>
                    </div>

                    <Form.Group className="mb-3">
                        <Form.Label className="fw-semibold">
                            {t("Payment Method")} <span className="text-danger">*</span>
                        </Form.Label>
                        <Form.Select
                            value={paymentForm.payment_method_id}
                            onChange={(e) => setPaymentForm({ ...paymentForm, payment_method_id: e.target.value })}
                            size="lg"
                        >
                            <option value="">{t("Select method")}</option>
                            {paymentMethods.map(pm => (
                                <option key={pm.id} value={pm.id}>{pm.name}</option>
                            ))}
                        </Form.Select>
                    </Form.Group>

                    <Form.Group className="mb-3">
                        <Form.Label className="fw-semibold">
                            {t("Amount")} <span className="text-danger">*</span>
                        </Form.Label>
                        <Form.Control
                            type="number"
                            step="0.01"
                            min="0.01"
                            max={purchase.financial_summary?.due_amount}
                            value={paymentForm.amount}
                            onChange={(e) => setPaymentForm({ ...paymentForm, amount: parseFloat(e.target.value) || 0 })}
                            size="lg"
                            placeholder="0.00"
                        />
                    </Form.Group>

                    <Form.Group className="mb-3">
                        <Form.Label className="fw-semibold">{t("Payment Date")}</Form.Label>
                        <Form.Control
                            type="date"
                            value={paymentForm.payment_date}
                            onChange={(e) => setPaymentForm({ ...paymentForm, payment_date: e.target.value })}
                            size="lg"
                        />
                    </Form.Group>

                    <Form.Group>
                        <Form.Label className="fw-semibold">{t("Notes")}</Form.Label>
                        <Form.Control
                            as="textarea"
                            rows={2}
                            value={paymentForm.notes}
                            onChange={(e) => setPaymentForm({ ...paymentForm, notes: e.target.value })}
                            placeholder={t("Optional notes...")}
                        />
                    </Form.Group>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => setShowPaymentModal(false)}>
                        {t("Cancel")}
                    </Button>
                    <Button
                        variant="primary"
                        onClick={handleAddPayment}
                        disabled={paymentSubmitting}
                        className="px-4"
                    >
                        {paymentSubmitting ? (
                            <>
                                <div className="spinner-border spinner-border-sm me-2" role="status" />
                                {t("Processing...")}
                            </>
                        ) : (
                            <>
                                <FiCheck className="me-2" /> {t("Add Payment")}
                            </>
                        )}
                    </Button>
                </Modal.Footer>
            </Modal>
        </PageLayout>
    );
}